import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Download, FileSpreadsheet, Loader2, Package,
  Plus, Hammer, Check, X, ListChecks, Database as DbIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
import { JwEwbCell, JwTransferPrintBtn, useJwEwb } from '@/components/jobWork/JwTransfer';
import { JobberPicker } from '@/components/jobbers/JobberPicker';
const ShirtMfgHub = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('jw-orders');

  // Master state
  const masterRef = useRef(null);
  const [masterRows, setMasterRows] = useState([]);
  const [assemblies, setAssemblies] = useState([]);

  // USN bank state
  const usnRef = useRef(null);
  const [bankStats, setBankStats] = useState({ available: 0, consumed: 0, total: 0 });

  // JW orders
  const [orders, setOrders] = useState([]);
  const [ewb] = useJwEwb('job_order', orders.map((o) => o.jw_id));
  const [showCreate, setShowCreate] = useState(false);
  const [newAssembly, setNewAssembly] = useState('');
  const [newQty, setNewQty] = useState('');
  const [newJobber, setNewJobber] = useState('');
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    const [m, a, b, o] = await Promise.all([
      apiClient.get('/shirt-mfg/master?limit=2000').catch(() => ({ data: { rows: [] } })),
      apiClient.get('/shirt-mfg/master/assemblies').catch(() => ({ data: { assemblies: [] } })),
      apiClient.get('/shirt-mfg/usn-bank/stats').catch(() => ({ data: {} })),
      apiClient.get('/shirt-mfg/jw-orders?limit=200').catch(() => ({ data: { orders: [] } })),
    ]);
    setMasterRows(m.data.rows || []);
    setAssemblies(a.data.assemblies || []);
    setBankStats(b.data || {});
    setOrders(o.data.orders || []);
  };
  useEffect(() => { refresh(); }, []);

  const uploadMaster = async (file, replace = false) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiClient.post(
        `/shirt-mfg/master/upload?replace_existing=${replace}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Inserted ${res.data.inserted} BOM rows`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} rows had errors`);
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) toast.error(detail.errors[0]);
      else toast.error(detail || 'Upload failed');
    }
  };

  const downloadTemplate = async () => {
    const res = await apiClient.get('/shirt-mfg/master/csv-template');
    const blob = new Blob([res.data.template], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'shirt_mfg_master_template.csv';
    a.click();
  };

  const uploadUsnBank = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiClient.post('/shirt-mfg/usn-bank/import-csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${res.data.imported} fresh USNs (${res.data.duplicates_skipped} duplicates skipped)`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'USN import failed');
    }
  };

  const createOrder = async () => {
    if (!newAssembly || !newQty) { toast.error('Assembly + qty required'); return; }
    setCreating(true);
    try {
      const res = await apiClient.post('/shirt-mfg/jw-orders', {
        assembly_item: newAssembly,
        target_qty: parseInt(newQty, 10),
        jobber_name: newJobber || null,
      });
      toast.success(`JW order ${res.data.jw_id} created`);
      setShowCreate(false);
      setNewAssembly(''); setNewQty(''); setNewJobber('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  return (
    <StaffScanGate stationName="Shirt Mfg — Hub">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="shirt-mfg-hub">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/shirt-manufacturing')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Shirt Manufacturing — Hub</h1>
              <p className="text-sm text-[#E8DCC8]">Job-Work Orders, BOM Master, USN Bank</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className="bg-emerald-100 text-emerald-700">USNs Available {bankStats.available || 0}</Badge>
            <Badge variant="outline">Assemblies {assemblies.length}</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="jw-orders" data-testid="tab-jw-orders">
              <Hammer className="w-4 h-4 mr-1" /> JW Orders
            </TabsTrigger>
            <TabsTrigger value="master" data-testid="tab-master">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> BOM Master
            </TabsTrigger>
            <TabsTrigger value="usn-bank" data-testid="tab-usn-bank">
              <DbIcon className="w-4 h-4 mr-1" /> USN Bank
            </TabsTrigger>
          </TabsList>

          {/* JW Orders */}
          <TabsContent value="jw-orders" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowCreate(true)}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="create-jw-order-btn">
                <Plus className="w-4 h-4 mr-2" /> New JW Order
              </Button>
            </div>
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>JW ID</TableHead>
                      <TableHead>Assembly</TableHead>
                      <TableHead>Target Qty</TableHead>
                      <TableHead>Jobber</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>E-way bill</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Open</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!orders.length && (
                      <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-6">
                        No JW orders yet
                      </TableCell></TableRow>
                    )}
                    {orders.map((o) => (
                      <TableRow key={o.jw_id} data-testid={`jw-row-${o.jw_id}`}>
                        <TableCell className="font-mono text-xs">{o.jw_id}</TableCell>
                        <TableCell>{o.assembly_item}</TableCell>
                        <TableCell>{o.target_qty}</TableCell>
                        <TableCell className="text-xs">{o.jobber_name || '—'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{o.status}</Badge></TableCell>
                        <TableCell><JwEwbCell s={ewb[o.jw_id]} refNo={o.jw_id} testId={`jw-ewb-${o.jw_id}`} /></TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(o.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap space-x-1">
                          <JwTransferPrintBtn kind="job_order" refNo={o.jw_id} s={ewb[o.jw_id]} label="Transfer" testId={`jw-transfer-print-${o.jw_id}`} />
                          <Button size="sm" variant="outline"
                            onClick={() => navigate(`/sections/shirt-mfg/jw-tagging/${o.jw_id}`)}
                            data-testid={`open-jw-${o.jw_id}`}>
                            Tag
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <EwbDocPanel docType="job_work" section="Shirt Mfg" title="JW order e-way bills" />
          </TabsContent>

          {/* BOM Master */}
          <TabsContent value="master" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Bill-of-Materials</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={downloadTemplate}
                      data-testid="download-master-template-btn">
                      <Download className="w-4 h-4 mr-2" /> Template
                    </Button>
                    <input type="file" hidden ref={masterRef} accept=".csv"
                      onChange={(e) => uploadMaster(e.target.files?.[0], false)}
                      data-testid="master-file-input" />
                    <Button onClick={() => masterRef.current?.click()}
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      data-testid="upload-master-btn">
                      <Upload className="w-4 h-4 mr-2" /> Upload CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Assembly</TableHead>
                      <TableHead>Component</TableHead>
                      <TableHead>Consumption Qty</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!masterRows.length && (
                      <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">
                        Upload a BOM master CSV to see entries here
                      </TableCell></TableRow>
                    )}
                    {masterRows.slice(0, 500).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{r.assembly_item}</TableCell>
                        <TableCell className="font-mono text-xs">{r.component_item}</TableCell>
                        <TableCell>{r.consumption_qty}</TableCell>
                        <TableCell className="text-xs text-gray-500">{r.remarks || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USN Bank */}
          <TabsContent value="usn-bank" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Replace USN Bank (section: Shirt Manufacturing)</CardTitle>
                  <div className="flex gap-2 items-center">
                    <Badge>Available {bankStats.available}</Badge>
                    <Badge variant="outline">Consumed {bankStats.consumed}</Badge>
                    <input type="file" hidden ref={usnRef} accept=".csv"
                      onChange={(e) => uploadUsnBank(e.target.files?.[0])}
                      data-testid="usn-bank-file-input" />
                    <Button onClick={() => usnRef.current?.click()}
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      data-testid="upload-usn-bank-btn">
                      <Upload className="w-4 h-4 mr-2" /> Import USNs (CSV)
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  Import a one-column CSV (header `usn_code` optional) of pre-printed USNs.
                  Each USN scanned at JW tagging draws one fresh USN from this bank.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* CREATE JW ORDER DIALOG */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Job Work Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Assembly Item *</Label>
              <Select value={newAssembly} onValueChange={setNewAssembly}>
                <SelectTrigger data-testid="new-assembly-trigger">
                  <SelectValue placeholder="Select assembly…" />
                </SelectTrigger>
                <SelectContent>
                  {assemblies.length === 0 && <SelectItem disabled value="none">Upload BOM Master first</SelectItem>}
                  {assemblies.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target Assembly Qty *</Label>
              <Input type="number" value={newQty} onChange={(e) => setNewQty(e.target.value)}
                placeholder="100" data-testid="new-target-qty" />
            </div>
            <div>
              <Label>Jobber</Label>
              <JobberPicker value={newJobber} onChange={setNewJobber} testId="new-jobber" allowClear />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createOrder} disabled={creating}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="confirm-create-jw-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </StaffScanGate>
  );
};

export default ShirtMfgHub;
